import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS } from '../config/apiRouting';
import './NetworkManagement.css';

interface NetworkSite {
  restaurantId: string;
  siteId: string;
  restaurantName: string;
  location: string;
  isActive: boolean;
  createdAt: string;
}

interface NetworkStats {
  site_name: string;
  uptime: number;
  num_ap: number;
  num_sw: number;
  num_gw: number;
  num_adopted: number;
  num_user: number;
  num_guest: number;
  tx_bytes: number;
  rx_bytes: number;
  total_clients: number;
  total_devices: number;
  status: string;
}

interface NetworkClient {
  mac: string;
  hostname?: string;
  ip: string;
  is_guest: boolean;
  is_wired: boolean;
  signal?: number;
  tx_bytes?: number;
  rx_bytes?: number;
  last_seen: number;
  uptime: number;
}

interface NetworkDevice {
  mac: string;
  name: string;
  model: string;
  type: string;
  ip: string;
  state: number;
  uptime: number;
  version: string;
}

const NetworkManagement: React.FC = () => {
  const [sites, setSites] = useState<NetworkSite[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(null);
  const [clients, setClients] = useState<NetworkClient[]>([]);
  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'clients' | 'devices' | 'video'>('overview');

  // Form state for new site configuration
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [connectionType, setConnectionType] = useState<'direct' | 'cloud' | 'both'>('both');
  const [siteForm, setSiteForm] = useState({
    restaurantId: '',
    restaurantName: '',
    location: '',
    controllerHost: '',
    controllerPort: '8443',
    username: '',
    password: '',
    siteName: 'default',
    // Cloud credentials
    cloudUsername: '',
    cloudPassword: '',
    useCloudOnly: false,
    // Video system
    videoControllerHost: '',
    videoUsername: '',
    videoPassword: '',
    videoPort: '7443',
    isProtect: true
  });

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      fetchNetworkData(selectedSite);
    }
  }, [selectedSite, activeTab]);

  const fetchSites = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.networkSites());
      if (response.ok) {
        const data = await response.json();
        setSites(data.sites || []);
        if (data.sites.length > 0 && !selectedSite) {
          setSelectedSite(data.sites[0].restaurantId);
        }
      }
    } catch (err) {
      setError('Failed to fetch network sites');
    }
  };

  const fetchNetworkData = async (restaurantId: string) => {
    setLoading(true);
    setError('');

    try {
      // Fetch network statistics
      const statsResponse = await fetch(API_ENDPOINTS.networkStats(restaurantId));
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setNetworkStats(statsData.stats);
      }

      if (activeTab === 'clients') {
        // Fetch active clients
        const clientsResponse = await fetch(API_ENDPOINTS.networkClients(restaurantId) + '?active_only=true');
        if (clientsResponse.ok) {
          const clientsData = await clientsResponse.json();
          setClients(clientsData.clients || []);
        }
      } else if (activeTab === 'devices') {
        // Fetch network devices
        const devicesResponse = await fetch(API_ENDPOINTS.networkDevices(restaurantId));
        if (devicesResponse.ok) {
          const devicesData = await devicesResponse.json();
          setDevices(devicesData.devices || []);
        }
      } else if (activeTab === 'video') {
        // Fetch cameras
        const camerasResponse = await fetch(API_ENDPOINTS.videoCameras(restaurantId));
        if (camerasResponse.ok) {
          const camerasData = await camerasResponse.json();
          setCameras(camerasData.cameras || []);
        }
      }
    } catch (err) {
      setError('Failed to fetch network data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted! Data:', siteForm);
    setLoading(true);
    setError('');

    try {
      const endpoint = API_ENDPOINTS.networkSites();
      console.log('Posting to endpoint:', endpoint);
      
      const payload = {
        restaurantId: siteForm.restaurantId,
        siteConfig: siteForm
      };
      console.log('Payload:', payload);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        console.log('Site created successfully!');
        setShowSiteForm(false);
        setSiteForm({
          restaurantId: '',
          restaurantName: '',
          location: '',
          controllerHost: '',
          controllerPort: '8443',
          username: '',
          password: '',
          siteName: 'default',
          cloudUsername: '',
          cloudPassword: '',
          useCloudOnly: false,
          videoControllerHost: '',
          videoUsername: '',
          videoPassword: '',
          videoPort: '7443',
          isProtect: true
        });
        setConnectionType('both');
        fetchSites();
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.error || `Failed to create site (${response.status})`);
        } catch {
          setError(`Failed to create site: ${response.status} - ${errorText}`);
        }
      }
    } catch (err) {
      console.error('Network error:', err);
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorizeDevice = async (macAddress: string) => {
    if (!selectedSite) return;

    try {
      const response = await fetch(API_ENDPOINTS.networkAuthorize(selectedSite), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          macAddress,
          deviceName: 'New Device'
        })
      });

      if (response.ok) {
        fetchNetworkData(selectedSite);
      } else {
        setError('Failed to authorize device');
      }
    } catch (err) {
      setError('Failed to authorize device');
    }
  };

  const handleBlockClient = async (macAddress: string, block: boolean) => {
    if (!selectedSite) return;

    try {
      const response = await fetch(API_ENDPOINTS.networkBlock(selectedSite), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          macAddress,
          block
        })
      });

      if (response.ok) {
        fetchNetworkData(selectedSite);
      } else {
        setError(`Failed to ${block ? 'block' : 'unblock'} client`);
      }
    } catch (err) {
      setError(`Failed to ${block ? 'block' : 'unblock'} client`);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <div className="network-management">
      <div className="header-section">
        <h1>Network Management</h1>
        <div className="header-controls">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="site-selector"
          >
            <option value="">Select a site</option>
            {sites.map((site) => (
              <option key={site.restaurantId} value={site.restaurantId}>
                {site.restaurantName} - {site.location}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              console.log('Add Site button clicked');
              setShowSiteForm(true);
            }}
            className="btn btn-primary"
          >
            Add Site
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {selectedSite && (
        <>
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`tab ${activeTab === 'clients' ? 'active' : ''}`}
              onClick={() => setActiveTab('clients')}
            >
              Clients
            </button>
            <button
              className={`tab ${activeTab === 'devices' ? 'active' : ''}`}
              onClick={() => setActiveTab('devices')}
            >
              Devices
            </button>
            <button
              className={`tab ${activeTab === 'video' ? 'active' : ''}`}
              onClick={() => setActiveTab('video')}
            >
              Video System
            </button>
          </div>

          {loading && <div className="loading">Loading...</div>}

          {activeTab === 'overview' && networkStats && (
            <div className="overview-section">
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Network Status</h3>
                  <div className="stat-value">{networkStats.status}</div>
                  <div className="stat-label">Overall Status</div>
                </div>
                <div className="stat-card">
                  <h3>Devices</h3>
                  <div className="stat-value">{networkStats.total_devices}</div>
                  <div className="stat-label">
                    {networkStats.num_ap} APs, {networkStats.num_sw} Switches, {networkStats.num_gw} Gateways
                  </div>
                </div>
                <div className="stat-card">
                  <h3>Clients</h3>
                  <div className="stat-value">{networkStats.total_clients}</div>
                  <div className="stat-label">
                    {networkStats.num_user} Users, {networkStats.num_guest} Guests
                  </div>
                </div>
                <div className="stat-card">
                  <h3>Traffic</h3>
                  <div className="stat-value">
                    ↓ {formatBytes(networkStats.rx_bytes)} ↑ {formatBytes(networkStats.tx_bytes)}
                  </div>
                  <div className="stat-label">Download / Upload</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'clients' && (
            <div className="clients-section">
              <div className="section-header">
                <h2>Connected Clients</h2>
                <div>
                  <input
                    type="text"
                    placeholder="Enter MAC address to authorize"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAuthorizeDevice(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                </div>
              </div>
              <div className="clients-table">
                {clients.map((client) => (
                  <div key={client.mac} className="client-row">
                    <div className="client-info">
                      <div className="client-name">{client.hostname || 'Unknown Device'}</div>
                      <div className="client-mac">{client.mac}</div>
                      <div className="client-details">
                        {client.ip} • {client.is_wired ? 'Wired' : 'Wireless'} • 
                        {client.signal && ` Signal: ${client.signal}dBm •`} 
                        Uptime: {formatUptime(client.uptime)}
                      </div>
                    </div>
                    <div className="client-actions">
                      <button
                        onClick={() => handleBlockClient(client.mac, true)}
                        className="btn btn-secondary btn-small"
                      >
                        Block
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'devices' && (
            <div className="devices-section">
              <h2>Network Devices</h2>
              <div className="devices-table">
                {devices.map((device) => (
                  <div key={device.mac} className="device-row">
                    <div className="device-info">
                      <div className="device-name">{device.name}</div>
                      <div className="device-model">{device.model}</div>
                      <div className="device-details">
                        {device.ip} • {device.type} • Version: {device.version} • 
                        Uptime: {formatUptime(device.uptime)}
                      </div>
                    </div>
                    <div className="device-status">
                      <span className={`status ${device.state === 1 ? 'online' : 'offline'}`}>
                        {device.state === 1 ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'video' && (
            <div className="video-section">
              <h2>Camera System</h2>
              <div className="cameras-grid">
                {cameras.map((camera) => (
                  <div key={camera.id || camera.mac} className="camera-card">
                    <div className="camera-info">
                      <h3>{camera.name || 'Camera'}</h3>
                      <div className="camera-details">
                        {camera.model} • {camera.state === 'CONNECTED' ? 'Online' : 'Offline'}
                      </div>
                    </div>
                    <div className="camera-actions">
                      <button
                        onClick={() => {
                          // Open snapshot in new tab
                          window.open(API_ENDPOINTS.videoSnapshot(selectedSite, camera.id), '_blank');
                        }}
                        className="btn btn-secondary btn-small"
                      >
                        Snapshot
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Site Creation Modal - Rendered as Portal */}
      {showSiteForm && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            border: '2px solid #ddd'
          }}>
            <div className="modal-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #eee'
            }}>
              <h2 style={{ margin: 0 }}>Add Network Site</h2>
              <button
                onClick={() => setShowSiteForm(false)}
                className="modal-close"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateSite} className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Restaurant ID</label>
                <input
                  type="text"
                  value={siteForm.restaurantId}
                  onChange={(e) => setSiteForm({ ...siteForm, restaurantId: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Restaurant Name</label>
                <input
                  type="text"
                  value={siteForm.restaurantName}
                  onChange={(e) => setSiteForm({ ...siteForm, restaurantName: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Location</label>
                <input
                  type="text"
                  value={siteForm.location}
                  onChange={(e) => setSiteForm({ ...siteForm, location: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Connection Type</label>
                <select
                  value={connectionType}
                  onChange={(e) => {
                    const type = e.target.value as 'direct' | 'cloud' | 'both';
                    setConnectionType(type);
                    setSiteForm({ 
                      ...siteForm, 
                      useCloudOnly: type === 'cloud' 
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="both">Try Direct, Fall back to Cloud</option>
                  <option value="direct">Direct Connection Only</option>
                  <option value="cloud">Cloud Connection Only</option>
                </select>
              </div>

              {(connectionType === 'direct' || connectionType === 'both') && (
                <>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Controller Host (IP/Domain)</label>
                    <input
                      type="text"
                      value={siteForm.controllerHost}
                      onChange={(e) => setSiteForm({ ...siteForm, controllerHost: e.target.value })}
                      placeholder="192.168.1.1 or mydomain.com"
                      required={connectionType === 'direct'}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Local Admin Username</label>
                    <input
                      type="text"
                      value={siteForm.username}
                      onChange={(e) => setSiteForm({ ...siteForm, username: e.target.value })}
                      placeholder="Local controller username"
                      required={connectionType === 'direct'}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Local Admin Password</label>
                    <input
                      type="password"
                      value={siteForm.password}
                      onChange={(e) => setSiteForm({ ...siteForm, password: e.target.value })}
                      placeholder="Local controller password"
                      required={connectionType === 'direct'}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </>
              )}

              {(connectionType === 'cloud' || connectionType === 'both') && (
                <>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>UniFi Cloud Email</label>
                    <input
                      type="email"
                      value={siteForm.cloudUsername}
                      onChange={(e) => setSiteForm({ ...siteForm, cloudUsername: e.target.value })}
                      placeholder="Your UniFi Cloud account email"
                      required={connectionType === 'cloud'}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>UniFi Cloud Password</label>
                    <input
                      type="password"
                      value={siteForm.cloudPassword}
                      onChange={(e) => setSiteForm({ ...siteForm, cloudPassword: e.target.value })}
                      placeholder="Your UniFi Cloud account password"
                      required={connectionType === 'cloud'}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </>
              )}
              <div className="form-actions" style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowSiteForm(false)} 
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    background: '#6c757d',
                    color: 'white'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading} 
                  className="btn btn-primary"
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    background: '#007bff',
                    color: 'white'
                  }}
                >
                  {loading ? 'Creating...' : 'Create Site'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NetworkManagement;